import React, { useState, useEffect } from 'react';
import Table from '../../components/Table/Table';
import ApiManager from '../../ApiManager/ApiManager';
import SearchBar from '../../components/SearchBar/SearchBar';
import FilterBar from '../../components/FilterBar/FilterBar';
import Bill from '../../components/Bill/Bill';
import { useNavigate } from 'react-router-dom';
import { hasPermission, ShowComponent } from '../../components/RoleBasedComponents';
import PERMISSIONS from '../../permissions';
import { useAuth } from '../../components/AuthProvider';
import { toast } from 'react-toastify';
import { useComponentLoading } from '../../utils/useComponentLoading';
import AddNewTrip from '../AddNewTrip/AddNewTrip';
import AddCompletedTrip from '../AddNewTrip/AddCompletedTrip';

function ShowTrips({ status }) {
  const [data, setData] = useState([]);
  const [rowId, setRowId] = useState(null);
  const [filters, setFilters] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); 
  const [hasMorePages, setHasMorePages] = useState(true);
  const { userPermissions } = useAuth();
  const navigate = useNavigate();
  const {
    isLoading,
    getMessage,
    getProgress,
    withLoading
  } = useComponentLoading();

  const fetchData = async () => {
    try {
      await withLoading('fetchTrips', async (updateProgress) => {
        updateProgress(10, 'Fetching trips...');
        
        const params = new URLSearchParams({
          TimeFrom: filters.startTime ? new Date(filters.startTime).getTime() / 1000 : 0,
          TimeTo: filters.endTime ? new Date(filters.endTime).getTime() / 1000 : 1993456000,
          PageSize: 20,
          PageNumber: currentPage,
          TripStatus: status,
          CompanyId: filters.companyId || '',
        });

        updateProgress(30, 'Fetching trips...');
        const response = await ApiManager.getAllTrips(params);
        if (!response.ok) throw new Error('Network response was not ok');
        
        updateProgress(60, 'Processing response...');
        const json = await response.json();
        console.log('res', json);

        updateProgress(90, 'Updating UI...');
        const formattedTrips = json.trips.map(trip => ({
          ...trip,
          actionButtons: getActionButtons(trip.tripStatus, trip.tripId)
        }));
        setData(formattedTrips);
        setHasMorePages(json.trips.length == 20);
        
        updateProgress(100, 'Trips loaded successfully');
      }, 'Loading trips...');
    } catch (error) {
      console.error('Error fetching trips:', error);
      toast.error('Failed to fetch trips');
      setData([]);
      setHasMorePages(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [status, filters, currentPage]);

  // Action button handlers
  const handleDetails = (tripId) => {
    if (tripId) {
      navigate(`/login/trips/booking-details/${tripId}`);
    }
  };

  const handleEdit = (tripId) => {
    if (tripId) {
      navigate(`/login/trips/booking-details/${tripId}`);
    }
  };

  // Function to generate buttons based on trip status
  const getActionButtons = (tripStatus, tripId) => {
    const buttons = [];
    
    // Common buttons for all statuses
    buttons.push({ label: "Details", onClick: () => handleDetails(tripId) });
    
    if (hasPermission(userPermissions, PERMISSIONS.VENDOR_TRIP_UPDATE)) {
      buttons.push({ label: "Edit", onClick: () => handleEdit(tripId) });
    }
    
    // Status-specific buttons
    switch (tripStatus) {
      case "CREATED":
        if (hasPermission(userPermissions, PERMISSIONS.VENDOR_TRIP_UPDATE)) {
          buttons.push({ 
            label: "Book Trip", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'book' } 
            })
          });
          buttons.push({ 
            label: "Cancel Trip", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'cancel' } 
            })
          });
        }
        break;
        
      case "BOOKED":
        if (hasPermission(userPermissions, PERMISSIONS.VENDOR_TRIP_UPDATE)) {
          buttons.push({ 
            label: "Assign Driver", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'assign' } 
            })
          });
          buttons.push({ 
            label: "Cancel Trip", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'cancel' } 
            })
          });
          buttons.push({ 
            label: "Send Booking Confirmation", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'booking_confirmation' } 
            })
          });
        }
        break;
        
      case "ALLOTTED":
        if (hasPermission(userPermissions, PERMISSIONS.VENDOR_TRIP_UPDATE)) {
          buttons.push({ 
            label: "Mark Dispatched", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'dispatch' } 
            })
          });
          buttons.push({ 
            label: "Cancel Trip", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'cancel' } 
            })
          });
          buttons.push({ 
            label: "Send Booking Confirmation", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'booking_confirmation' } 
            })
          });
          buttons.push({ 
            label: "Send Driver Alloted Notification", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'driver_alloted_notification' } 
            })
          });
        }
        break;
        
      case "DISPATCHED":
        if (hasPermission(userPermissions, PERMISSIONS.VENDOR_TRIP_UPDATE)) {
          buttons.push({ 
            label: "Complete Trip", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'complete' } 
            })
          });
          buttons.push({ 
            label: "Cancel Trip", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'cancel' } 
            })
          });
          buttons.push({ 
            label: "Send Booking Confirmation", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'booking_confirmation' } 
            })
          });
          buttons.push({ 
            label: "Send Driver Alloted Notification", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'driver_alloted_notification' } 
            })
          });
        }
        break;
        
      case "COMPLETED":
        if (hasPermission(userPermissions, PERMISSIONS.VENDOR_TRIP_UPDATE)) {
          buttons.push({ 
            label: "Cancel Trip", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'cancel' } 
            })
          });
          buttons.push({ 
            label: "Generate Invoice", 
            onClick: () => navigate(`/login/draft_invoice`, {
              state: {
                tripIds: [tripId],
              },
            })
          });
        }
        break;
        
      case "BILLED":
        if (hasPermission(userPermissions, PERMISSIONS.VENDOR_TRIP_UPDATE)) {
          buttons.push({ 
            label: "Cancel Trip", 
            onClick: () => navigate(`/login/trips/booking-details/${tripId}`, { 
              state: { action: 'cancel' } 
            })
          });
        }
        break;
        
      default:
        break;
    }
    
    return buttons;
  };

  const handleRowClick = (row) => {
    if (row) {
      navigate(`/login/trips/booking-details/${row}`);
    } else {
      console.error("No row selected");
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "created":
        return "#2196F3"; // Blue
      case "booked":
        return "#4CAF50"; // Green
      case "allotted":
        return "#FF9800"; // Orange
      case "dispatched":
        return "#E91E63"; // Pink
      case "completed":
        return "#00BCD4"; // Cyan
      case "billed":
        return "#607D8B"; // Blue Grey
      case "cancelled":
        return "#F44336"; // Red
      default:
        return "#9E9E9E"; // Grey
    }
  };

  const getDriverStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "on_the_way":
        return "#9E9E9E"; // Amber
      case "started":
        return "#4CAF50"; // Green
      case "ended":
        return "#9C27B0"; // Purple
      case "completed":
        return "#00BCD4"; // Cyan
      default:
        return "#9E9E9E"; // Grey
    }
  };

  const columnsMapping = {
    ALL: [
      { field: 'select', headerName: 'Select' },
      { field: 'id', headerName: 'Trip ID' },
      { field: 'companyName', headerName: 'Company Name' },
      { field: 'driverName', headerName: 'Driver Name' },
      { field: 'carNumber', headerName: 'Car Number' },
      { field: 'fromCity', headerName: 'From City' },
      { field: 'toCity', headerName: 'To City' },
      { 
        field: 'tripStatus', 
        headerName: 'Status',
        renderCell: ({ row }) => (
          <span
            style={{
              backgroundColor: getStatusColor(row.tripStatus),
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "12px",
              fontSize: "0.9em",
              fontWeight: "bold",
            }}
          >
            {row.tripStatus}
          </span>
        )
      },
    ],
    CREATED: [
      { field: 'select', headerName: 'Select' },
      { field: 'id', headerName: 'Trip ID' },
      { field: 'companyName', headerName: 'Company Name' },
      { field: 'fromCity', headerName: 'From City' },
      { field: 'toCity', headerName: 'To City' },
      { 
        field: 'tripStatus', 
        headerName: 'Status',
        renderCell: ({ row }) => (
          <span
            style={{
              backgroundColor: getStatusColor(row.tripStatus),
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "12px",
              fontSize: "0.9em",
              fontWeight: "bold",
            }}
          >
            {row.tripStatus}
          </span>
        )
      },
    ],
    BOOKED: [
      { field: 'select', headerName: 'Select' },
      { field: 'id', headerName: 'Trip ID' },
      { field: 'companyName', headerName: 'Company Name' },
      { field: 'reportingTime', headerName: 'Reporting Time' },
      { field: 'dropAddress', headerName: 'Drop Address' },
      { 
        field: 'tripStatus', 
        headerName: 'Status',
        renderCell: ({ row }) => (
          <span
            style={{
              backgroundColor: getStatusColor(row.tripStatus),
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "12px",
              fontSize: "0.9em",
              fontWeight: "bold",
            }}
          >
            {row.tripStatus}
          </span>
        )
      },
    ],
    BILLED: [ 
      { field: 'select', headerName: 'Select'},
      { field: 'id', headerName: 'Trip ID' },
      { field: 'companyName', headerName: 'Company Name' },
      { field: 'driverName', headerName: 'Driver Name' },
      { field: 'carNumber', headerName: 'Car Number' },
      { field: 'bill', headerName: 'Bill' },
      { 
        field: 'tripStatus', 
        headerName: 'Status',
        renderCell: ({ row }) => (
          <span
            style={{
              backgroundColor: getStatusColor(row.tripStatus),
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "12px",
              fontSize: "0.9em",
              fontWeight: "bold",
            }}
          >
            {row.tripStatus}
          </span>
        )
      },
    ],
    ALLOTTED: [ 
      { field: 'select', headerName: 'Select'},
      { field: 'id', headerName: 'Trip ID' },
      { field: 'companyName', headerName: 'Company Name' },
      { field: 'driverName', headerName: 'Driver Name' },
      { field: 'carNumber', headerName: 'Car Number' },
      { field: 'driverAccepted', headerName: 'Driver Accepted' },
      { 
        field: 'tripStatus', 
        headerName: 'Status',
        renderCell: ({ row }) => (
          <span
            style={{
              backgroundColor: getStatusColor(row.tripStatus),
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "12px",
              fontSize: "0.9em",
              fontWeight: "bold",
            }}
          >
            {row.tripStatus}
          </span>
        )
      },
    ],
    DISPATCHED: [ 
      { field: 'select', headerName: 'Select'},
      { field: 'id', headerName: 'Trip ID' },
      { field: 'companyName', headerName: 'Company Name' },
      { field: 'driverName', headerName: 'Driver Name' },
      { field: 'carNumber', headerName: 'Car Number' },
      { 
        field: 'driverStatus', 
        headerName: 'Driver Status',
        renderCell: ({ row }) => (
          <span
            style={{
              backgroundColor: getDriverStatusColor(row.driverStatus),
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "12px",
              fontSize: "0.9em",
              fontWeight: "bold",
            }}
          >
            {row.driverStatus?.replace(/_/g, ' ')}
          </span>
        )
      },
      { 
        field: 'tripStatus', 
        headerName: 'Status',
        renderCell: ({ row }) => (
          <span
            style={{
              backgroundColor: getStatusColor(row.tripStatus),
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "12px",
              fontSize: "0.9em",
              fontWeight: "bold",
            }}
          >
            {row.tripStatus}
          </span>
        )
      },
    ],
  };

  const columns = columnsMapping[status] || columnsMapping.ALL;

  const rows = (data || []).map(item => ({
    id: item.tripId,
    companyName: item.company?.name,
    driverName: item.driverTrip?.driver?.name,
    carNumber: item.driverTrip?.car?.vehicleNo,
    passengerName: item.passengers?.[0] ? item.passengers[0].name : "",
    fromCity: item.fromCity,
    toCity: item.toCity,
    reportingTime: new Date(item.reportingTime * 1000).toLocaleString(),
    dropAddress: item.dropAddress,
    tripStatus: item.tripStatus,
    driverAccepted: item.driverTrip?.hasAccepted,
    driverStatus: item.driverTrip?.tripStatus,
    bill: 'Download',
    // Add action buttons for each row
    actionButtons: item.actionButtons
  }));

  const filterOptions = [
    { value: 'company', label: 'Company' },
    { value: 'time', label: 'Time Range' }
  ];

  const apiFunctions = {
    company: ApiManager.getAllCompanies,
  };

  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
  };

  return (
    <div className="show-trips">
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', paddingTop: '20px', alignItems: 'flex-start', height: '5vh' }}>
        <FilterBar filterOptions={filterOptions} onFilterChange={handleFilterChange} apiFunctions={apiFunctions} />
        {/* <SearchBar /> */}
        

        <div style={{ display: 'flex', gap: '10px' }}>
        <ShowComponent permission={PERMISSIONS.VENDOR_INVOICE_CREATE}>
          {status === "COMPLETED" && <Bill status={status} filters={filters} selectedRows={selectedRows} />}
        </ShowComponent>

          <ShowComponent permission={PERMISSIONS.VENDOR_TRIP_CREATE}>
                  <button className="custom-button" onClick={() => {
                    navigate('/login/trips/add-new');
                  }}>
                      Add New Trip
                  </button>
          </ShowComponent>

          <ShowComponent permission={PERMISSIONS.VENDOR_TRIP_COMPLETED_CREATE}>
              <button className="custom-button" onClick={() => {
                navigate('/login/trips/add-completed');
              }}>
                Add Completed Trip
              </button>
          </ShowComponent>
        </div>
      </div>

      <Table
        table_columns={columns}
        rows={rows}
        setRowsId={setRowId}
        onRowClick={handleRowClick}
        setSelectedRowId={setSelectedRows}
        fetchData={fetchData}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        hasMorePages={hasMorePages}
        isLoading={isLoading('fetchTrips')}
        loadingMessage={getMessage('fetchTrips')}
        loadingProgress={getProgress('fetchTrips')}
      />
    </div>
  );
}

export default ShowTrips;
